import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import ClassSelector from '../../components/ClassSelector';
import { TopicTrendsChart } from '../../components/Dashboard/AnalyticsCharts';
import useClassContext from '../../hooks/useClassContext';
import { teacherNavItems } from '../../constants/navigation';

const PERIOD_OPTIONS = [
  { label: '7 Days', value: 'last_7_days' },
  { label: '30 Days', value: 'last_30_days' },
  { label: '90 Days', value: 'last_90_days' },
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * AnalyticsPage - Dedicated analytics page for teachers
 */
const AnalyticsPage = () => {
  const { currentUser } = useAuth();
  const { selectedClassId, setSelectedClassId, appendClassFilter } = useClassContext();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [classSections, setClassSections] = useState([]);
  const [period, setPeriod] = useState('last_30_days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch class sections from dashboard endpoint
      const dashData = await apiRequest(appendClassFilter('/api/teacher/dashboard'), 'GET');
      if (dashData.class_sections) setClassSections(dashData.class_sections);

      // Fetch analytics with current period
      const teacherId = currentUser?.id;
      if (!teacherId) return;

      let url = `/api/teacher/analytics/class/${teacherId}?period=${period}`;
      if (selectedClassId) url += `&class_section_id=${selectedClassId}`;

      const data = await apiRequest(url, 'GET');
      setAnalyticsData(data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedClassId, period, currentUser?.id, appendClassFilter]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // CSV Export
  const handleExportCSV = () => {
    if (!analyticsData) return;

    const students = analyticsData.students || [];
    const headers = ['Name', 'Username', 'Grade', 'Sessions', 'Messages', 'Engagement Score', 'Streak Days', 'Last Active'];
    const rows = students.map(s => [
      `${s.first_name || ''} ${s.last_name || ''}`.trim(),
      s.username,
      s.grade_level || '',
      s.sessions_count || 0,
      s.messages_sent || 0,
      s.engagement_score || 0,
      s.streak_days || 0,
      s.last_active || 'Never'
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_${period}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout title="Analytics" navItems={teacherNavItems} userRole="teacher" userName={currentUser?.full_name || 'Teacher'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading analytics...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Analytics" navItems={teacherNavItems} userRole="teacher" userName={currentUser?.full_name || 'Teacher'}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading analytics: {error}
        </div>
      </DashboardLayout>
    );
  }

  const dailyEngagement = analyticsData?.daily_engagement || [];
  const hourlyUsage = analyticsData?.hourly_usage || [];
  const studentTrajectories = analyticsData?.student_trajectories || [];
  const students = analyticsData?.students || [];
  const topTopics = analyticsData?.class_trends?.most_asked_topics || [];

  return (
    <DashboardLayout title="Analytics" navItems={teacherNavItems} userRole="teacher" userName={currentUser?.full_name || 'Teacher'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-900">Class Analytics</h2>
        <button
          onClick={handleExportCSV}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {classSections.length > 0 && (
          <ClassSelector
            classSections={classSections}
            selectedClassId={selectedClassId}
            onClassChange={setSelectedClassId}
          />
        )}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                period === opt.value
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Total Students</p>
          <p className="text-2xl font-bold text-gray-900">{analyticsData?.student_metrics?.total_students || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Active Students</p>
          <p className="text-2xl font-bold text-green-600">{analyticsData?.student_metrics?.active_students || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Engagement Rate</p>
          <p className="text-2xl font-bold text-indigo-600">{(analyticsData?.student_metrics?.engagement_rate || 0).toFixed(1)}%</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Avg Engagement Score</p>
          <p className="text-2xl font-bold text-gray-900">{(analyticsData?.student_metrics?.average_engagement_score || 0).toFixed(1)}</p>
        </div>
      </div>

      {/* Engagement Over Time */}
      {dailyEngagement.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Over Time</h3>
          <div className="flex items-end gap-0.5 h-40">
            {dailyEngagement.map((day, index) => {
              const maxScore = Math.max(...dailyEngagement.map(d => d.avg_score), 1);
              const height = (day.avg_score / maxScore) * 100;
              return (
                <div
                  key={index}
                  className="flex-1 group relative"
                >
                  <div
                    className="bg-indigo-400 hover:bg-indigo-600 rounded-t transition-all w-full"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  />
                  <div className="hidden group-hover:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                    {day.date}: Score {day.avg_score}, {day.active_students} active
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>{dailyEngagement[0]?.date}</span>
            <span>{dailyEngagement[dailyEngagement.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Session Frequency Distribution */}
      {students.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Frequency by Student</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {students
              .sort((a, b) => (b.sessions_count || 0) - (a.sessions_count || 0))
              .map((student) => {
                const maxSessions = Math.max(...students.map(s => s.sessions_count || 0), 1);
                const pct = ((student.sessions_count || 0) / maxSessions) * 100;
                const engagement = student.engagement_score || 0;
                const barColor = engagement >= 80 ? 'bg-green-500' : engagement >= 60 ? 'bg-yellow-500' : engagement >= 40 ? 'bg-orange-500' : 'bg-red-500';
                return (
                  <div key={student.id || student.student_id} className="flex items-center gap-3">
                    <div className="w-28 text-sm text-gray-700 font-medium truncate" title={`${student.first_name} ${student.last_name}`}>
                      {student.first_name || student.username}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 relative">
                      <div
                        className={`${barColor} h-5 rounded-full transition-all flex items-center justify-end px-2`}
                        style={{ width: `${Math.max(pct, 5)}%` }}
                      >
                        <span className="text-xs text-white font-semibold">{student.sessions_count || 0}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}

      {/* Time-of-Day Usage Heatmap */}
      {hourlyUsage.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Time-of-Day Usage Heatmap</h3>
          <HeatmapGrid data={hourlyUsage} />
        </div>
      )}

      {/* Topic Mastery Progression */}
      {topTopics.length > 0 && (
        <div className="mb-8">
          <TopicTrendsChart topics={topTopics} />
        </div>
      )}

      {/* Student Trajectories */}
      {studentTrajectories.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Student Trajectories</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Student</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">This Week</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Last Week</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Trend</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">Last 7 Days</th>
                </tr>
              </thead>
              <tbody>
                {studentTrajectories.map((t) => (
                  <tr key={t.student_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium text-gray-900">{t.name}</td>
                    <td className="text-center py-2 px-3">{t.weekly_sessions[0]}</td>
                    <td className="text-center py-2 px-3">{t.weekly_sessions[1]}</td>
                    <td className="text-center py-2 px-3">
                      <span className={`text-sm font-semibold ${
                        t.trend === 'up' ? 'text-green-600' : t.trend === 'down' ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {t.trend === 'up' ? '\u25B2 Up' : t.trend === 'down' ? '\u25BC Down' : '\u2014 Flat'}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <Sparkline data={t.sparkline} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

/** Mini sparkline using CSS bars */
const Sparkline = ({ data }) => {
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-0.5 h-6 justify-center">
      {data.map((val, i) => (
        <div
          key={i}
          className="w-2 bg-indigo-400 rounded-t"
          style={{ height: `${Math.max((val / max) * 100, 8)}%` }}
          title={`${val} sessions`}
        />
      ))}
    </div>
  );
};

/** 7x18 heatmap grid (days x hours 6am-11pm) */
const HeatmapGrid = ({ data }) => {
  // Build a lookup: grid[dow][hour] = count
  const grid = {};
  let maxCount = 1;
  for (const item of data) {
    if (!grid[item.day_of_week]) grid[item.day_of_week] = {};
    grid[item.day_of_week][item.hour] = item.session_count;
    if (item.session_count > maxCount) maxCount = item.session_count;
  }

  const hours = [];
  for (let h = 6; h <= 23; h++) hours.push(h);

  const getColor = (count) => {
    if (!count || count === 0) return 'bg-gray-100';
    const intensity = count / maxCount;
    if (intensity > 0.75) return 'bg-indigo-700';
    if (intensity > 0.5) return 'bg-indigo-500';
    if (intensity > 0.25) return 'bg-indigo-300';
    return 'bg-indigo-100';
  };

  return (
    <div>
      {/* Hour labels */}
      <div className="flex gap-0.5 mb-1 ml-10">
        {hours.map(h => (
          <div key={h} className="flex-1 text-center text-xs text-gray-500">
            {h % 3 === 0 ? `${h > 12 ? h - 12 : h}${h >= 12 ? 'p' : 'a'}` : ''}
          </div>
        ))}
      </div>
      {/* Grid rows */}
      {[0, 1, 2, 3, 4, 5, 6].map(dow => (
        <div key={dow} className="flex items-center gap-0.5 mb-0.5">
          <div className="w-10 text-xs text-gray-600 text-right pr-1">{DAY_NAMES[dow]}</div>
          {hours.map(h => {
            const count = grid[dow]?.[h] || 0;
            return (
              <div
                key={h}
                className={`flex-1 h-5 rounded-sm ${getColor(count)} transition-colors`}
                title={`${DAY_NAMES[dow]} ${h}:00 - ${count} sessions`}
              />
            );
          })}
        </div>
      ))}
      {/* Legend */}
      <div className="flex items-center gap-2 mt-3 ml-10 text-xs text-gray-500">
        <span>Less</span>
        <div className="w-4 h-4 rounded-sm bg-gray-100 border border-gray-200" />
        <div className="w-4 h-4 rounded-sm bg-indigo-100" />
        <div className="w-4 h-4 rounded-sm bg-indigo-300" />
        <div className="w-4 h-4 rounded-sm bg-indigo-500" />
        <div className="w-4 h-4 rounded-sm bg-indigo-700" />
        <span>More</span>
      </div>
    </div>
  );
};

export default AnalyticsPage;
